import React, { useMemo } from 'react';
import { findPhoneNumbersInText } from 'libphonenumber-js';

const ignoreTags = new Set(['a', 'button']);

function stringToElements(str: string): React.ReactNode[] {
  const phoneNumbers = findPhoneNumbersInText(str, { defaultCountry: 'US' });
  const nodes: React.ReactNode[] = [];

  if (phoneNumbers.length === 0) {
    // no phone numbers found in the text, just return the string
    return [str];
  }

  let strIndex = 0;
  phoneNumbers.forEach(({ startsAt, endsAt, number }, index) => {
    if (strIndex < startsAt) {
      nodes.push(str.slice(strIndex, startsAt));
    }

    nodes.push(
      <a key={`clickablephone-${index}`} href={number.getURI()} rel="nofollow">
        {str.slice(startsAt, endsAt)}
      </a>
    );
    strIndex = endsAt;
  });

  // push final portion of string if it wasn't a phone number
  if (strIndex < str.length) {
    nodes.push(str.slice(strIndex));
  }

  return nodes;
}

function convertPhoneNumbersToLinks(element: React.ReactElement): React.ReactNode {
  if (React.Children.count(element.props.children) === 0) {
    return element;
  }

  const newChildren: React.ReactNode[] = [];

  React.Children.forEach(element.props.children, (child: React.ReactNode) => {
    if (typeof child === 'string') {
      newChildren.push(...stringToElements(child));
    } else if (React.isValidElement(child) && !ignoreTags.has(child.type.toString().toLowerCase())) {
      newChildren.push(convertPhoneNumbersToLinks(child));
    } else {
      // Unknown element type, just push
      newChildren.push(child);
    }
  });

  return React.cloneElement(element, { ...element.props }, ...newChildren);
}

export const LinkifyPhoneNumbers: React.FC = ({ children }) => {
  const newChildren = useMemo(() => {
    return <>{convertPhoneNumbersToLinks(<>{children}</>)}</>;
  }, [children]);

  return newChildren;
};
